import { useEffect, useState } from "react";
import { AdminShell } from "./AdminShell";
import {
    adminGetBusinessSettings,
    adminUpdateBusinessSettings,
    adminListDiscountCodes,
    adminCreateDiscountCode,
    adminUpdateDiscountCode,
    adminDeleteDiscountCode,
    adminPrintTest,
    adminPrintRecent,
    adminPrintClearQueue,
    adminPrintReprint,
} from "@/lib/api";
import { formatMoney } from "@/lib/orderUtils";
import { StaffPinsSection } from "@/components/StaffPinsSection";

const Section = ({ title, children, eyebrow }) => (
    <section className="mb-10">
        {eyebrow && <p className="eyebrow mb-2">{eyebrow}</p>}
        <h2 className="font-display text-[28px] leading-[0.95] mb-5">{title}</h2>
        {children}
    </section>
);

const AdminBusinessSettings = () => {
    const [settings, setSettings] = useState(null);
    const [saving, setSaving] = useState(false);
    const [codes, setCodes] = useState([]);
    const [showNewCode, setShowNewCode] = useState(false);

    const load = async () => {
        const [s, c] = await Promise.all([adminGetBusinessSettings(), adminListDiscountCodes()]);
        setSettings(s);
        setCodes(c);
    };

    useEffect(() => { load(); }, []);

    const save = async () => {
        if (!settings) return;
        setSaving(true);
        try {
            const updated = await adminUpdateBusinessSettings({
                kitchen_postcode: settings.kitchen_postcode,
                kitchen_address: settings.kitchen_address,
                delivery_zones: settings.delivery_zones,
                deliveries_enabled: !!settings.deliveries_enabled,
                kiosk_enabled: settings.kiosk_enabled !== false,
                small_order_threshold: parseFloat(settings.small_order_threshold) || 0,
                small_order_fee: parseFloat(settings.small_order_fee) || 0,
                service_fee_pct: parseFloat(settings.service_fee_pct) || 0,
                service_fee_min: parseFloat(settings.service_fee_min) || 0,
                service_fee_max: parseFloat(settings.service_fee_max) || 0,
                service_fee_fixed: parseFloat(settings.service_fee_fixed) || 0,
                bag_charge: parseFloat(settings.bag_charge) || 0,
                epson_printer_id: (settings.epson_printer_id || "counter").trim() || "counter",
                star_printer_id: (settings.star_printer_id || "").trim(),
            });
            setSettings(updated);
            alert("Saved ✓");
        } catch (e) {
            alert(e.response?.data?.detail || "Save failed");
        } finally { setSaving(false); }
    };

    if (!settings) {
        return <AdminShell><div className="text-muted">Loading…</div></AdminShell>;
    }

    const addZone = () => setSettings({ ...settings, delivery_zones: [...settings.delivery_zones, { postcode_prefix: "", fee: 0 }] });
    const removeZone = (i) => setSettings({ ...settings, delivery_zones: settings.delivery_zones.filter((_, idx) => idx !== i) });
    const updateZone = (i, patch) => setSettings({
        ...settings,
        delivery_zones: settings.delivery_zones.map((z, idx) => idx === i ? { ...z, ...patch } : z),
    });

    return (
        <AdminShell>
            <div className="max-w-3xl">
                <p className="eyebrow">// Naami Kitchens</p>
                <h1 className="font-display text-[44px] leading-[0.95] mb-2">
                    Business <span className="text-gold">settings.</span>
                </h1>
                <p className="text-muted text-[14px] mb-10">
                    These fees apply across every brand, every surface (QR, Kiosk, Counter).
                </p>

                <Section eyebrow="// Kitchen" title="Where you are">
                    <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted block mb-2">Kitchen postcode</label>
                    <input className="input mb-3" value={settings.kitchen_postcode} onChange={(e) => setSettings({ ...settings, kitchen_postcode: e.target.value })} data-testid="kitchen-postcode" />
                    <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted block mb-2">Full address (for receipts)</label>
                    <textarea className="input" rows="2" value={settings.kitchen_address} onChange={(e) => setSettings({ ...settings, kitchen_address: e.target.value })} placeholder="e.g. 12 Granton Square, Edinburgh EH5 1PR" />
                </Section>

                <Section eyebrow="// Kiosk" title="Walk-in kiosk on/off">
                    <div className="border-2 border-[var(--border)] p-4 mb-8 flex items-center justify-between gap-4" data-testid="kiosk-toggle-row">
                        <div className="min-w-0">
                            <div className="font-display text-[18px] leading-tight mb-0.5">Accept kiosk orders</div>
                            <p className="text-muted text-[13px] leading-snug">
                                When <strong>off</strong>, the walk-in <code>/kiosk</code> route immediately redirects to <code>order.naamikitchens.com/order</code>. Backend rejects any kiosk order POST. Use when the counter is short-staffed or the kiosk hardware is down.
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={() => setSettings({ ...settings, kiosk_enabled: !(settings.kiosk_enabled !== false) })}
                            className={`shrink-0 w-16 h-9 rounded-full relative transition-colors ${settings.kiosk_enabled !== false ? "bg-[var(--gold)]" : "bg-[var(--border)]"}`}
                            data-testid="kiosk-enabled-toggle"
                            aria-pressed={settings.kiosk_enabled !== false}
                            title={settings.kiosk_enabled !== false ? "Kiosk ON — click to disable" : "Kiosk OFF — click to enable"}
                        >
                            <span
                                className={`absolute top-1 w-7 h-7 bg-[var(--bg)] rounded-full transition-all ${settings.kiosk_enabled !== false ? "left-8" : "left-1"}`}
                            />
                        </button>
                    </div>
                </Section>

                <Section eyebrow="// Hub logins" title="Staff &amp; manager PINs">
                    <StaffPinsSection />
                </Section>

                <Section eyebrow="// Delivery" title="Delivery on/off &amp; postcode zones">
                    <div className="border-2 border-[var(--border)] p-4 mb-6 flex items-center justify-between gap-4" data-testid="deliveries-toggle-row">
                        <div className="min-w-0">
                            <div className="font-display text-[18px] leading-tight mb-0.5">Accept delivery orders</div>
                            <p className="text-muted text-[13px] leading-snug">
                                When <strong>off</strong>, delivery is hidden from every customer surface (QR, Kiosk, Multi-brand) and the backend rejects any lingering &quot;delivery&quot; POST. Existing scheduled delivery orders stay in the queue.
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={() => setSettings({ ...settings, deliveries_enabled: !settings.deliveries_enabled })}
                            className={`shrink-0 w-16 h-9 rounded-full relative transition-colors ${settings.deliveries_enabled ? "bg-[var(--gold)]" : "bg-[var(--border)]"}`}
                            data-testid="deliveries-enabled-toggle"
                            aria-pressed={!!settings.deliveries_enabled}
                            title={settings.deliveries_enabled ? "Deliveries ON — click to disable" : "Deliveries OFF — click to enable"}
                        >
                            <span
                                className={`absolute top-1 w-7 h-7 bg-[var(--bg)] rounded-full transition-all ${settings.deliveries_enabled ? "left-8" : "left-1"}`}
                            />
                        </button>
                    </div>
                    <p className="text-muted text-[13px] mb-4">
                        Add a row for each postcode prefix you deliver to. Longest match wins (so &quot;EH51&quot; would beat &quot;EH5&quot;).
                        Customers entering a postcode not on this list see &quot;Sorry, we don&apos;t deliver here.&quot;
                    </p>
                    <div className="space-y-2 mb-3" data-testid="zones-list">
                        {settings.delivery_zones.map((z, i) => (
                            <div key={i} className="flex gap-2 items-center">
                                <input
                                    className="input uppercase text-sm h-10 min-w-[140px] flex-1"
                                    placeholder="EH5"
                                    value={z.postcode_prefix}
                                    onChange={(e) => updateZone(i, { postcode_prefix: e.target.value.toUpperCase() })}
                                />
                                <span className="font-mono text-[11px] text-muted">£</span>
                                <input
                                    className="input w-24 text-sm h-10"
                                    type="number"
                                    step="0.5"
                                    value={z.fee}
                                    onChange={(e) => updateZone(i, { fee: parseFloat(e.target.value) || 0 })}
                                />
                                <button onClick={() => removeZone(i)} className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted hover:text-[#ff5246] px-2">✕</button>
                            </div>
                        ))}
                    </div>
                    <button onClick={addZone} className="btn-secondary text-sm" data-testid="add-zone">+ Add zone</button>
                </Section>

                <Section eyebrow="// Small orders" title="Small-order fee (delivery only)">
                    <p className="text-muted text-[13px] mb-4">
                        Charged <strong>once per delivery order</strong> when the whole basket subtotal is under the threshold. Applies to the combined basket total, not per brand. Collection orders never get this fee. Leave threshold at 0 to disable.
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted block mb-2">If basket under (£)</label>
                            <input className="input" type="number" step="0.5" value={settings.small_order_threshold} onChange={(e) => setSettings({ ...settings, small_order_threshold: e.target.value })} data-testid="small-threshold" />
                        </div>
                        <div>
                            <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted block mb-2">Flat fee (£)</label>
                            <input className="input" type="number" step="0.5" value={settings.small_order_fee} onChange={(e) => setSettings({ ...settings, small_order_fee: e.target.value })} data-testid="small-fee" />
                        </div>
                    </div>
                </Section>

                <Section eyebrow="// Service" title="Service fee">
                    <p className="text-muted text-[13px] mb-4">
                        Charged on every order (collection & delivery). Calculated as a percentage of subtotal, then clamped between a floor and a cap. Default: 10% with £0.99 minimum and £2.49 maximum.
                    </p>
                    <div className="grid grid-cols-3 gap-3">
                        <div>
                            <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted block mb-2">Percentage (%)</label>
                            <input className="input" type="number" step="0.5" value={settings.service_fee_pct} onChange={(e) => setSettings({ ...settings, service_fee_pct: e.target.value })} data-testid="service-pct" />
                        </div>
                        <div>
                            <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted block mb-2">Minimum (£)</label>
                            <input className="input" type="number" step="0.05" value={settings.service_fee_min ?? ""} onChange={(e) => setSettings({ ...settings, service_fee_min: e.target.value })} data-testid="service-min" />
                        </div>
                        <div>
                            <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted block mb-2">Maximum (£)</label>
                            <input className="input" type="number" step="0.05" value={settings.service_fee_max ?? ""} onChange={(e) => setSettings({ ...settings, service_fee_max: e.target.value })} data-testid="service-max" />
                        </div>
                    </div>
                </Section>

                <Section eyebrow="// Packaging" title="Bag charge">
                    <p className="text-muted text-[13px] mb-4">One bag charge per order. Set to 0 to disable.</p>
                    <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted block mb-2">Bag (£)</label>
                    <input className="input max-w-[200px]" type="number" step="0.05" value={settings.bag_charge} onChange={(e) => setSettings({ ...settings, bag_charge: e.target.value })} data-testid="bag-charge" />
                </Section>

                <EpsonSection
                    settings={settings}
                    setSettings={setSettings}
                />

                <StarSection
                    settings={settings}
                    setSettings={setSettings}
                />

                <RecentSlipsSection />

                <button onClick={save} disabled={saving} className="btn-primary text-base px-8 py-3 mb-12" data-testid="save-settings">
                    {saving ? "Saving…" : "Save settings →"}
                </button>

                {/* Discount codes */}
                <Section eyebrow="// Savings &amp; offers" title="Discount codes">
                    <div className="border border-[var(--border)] divide-y divide-[var(--border)] mb-3" data-testid="codes-list">
                        {codes.length === 0 && (
                            <div className="p-4 text-center text-muted text-sm">No codes yet.</div>
                        )}
                        {codes.map((c) => (
                            <DiscountRow key={c.id} code={c} onSaved={load} />
                        ))}
                    </div>
                    {!showNewCode ? (
                        <button onClick={() => setShowNewCode(true)} className="btn-secondary text-sm" data-testid="add-code-btn">
                            + Add discount code
                        </button>
                    ) : (
                        <NewCodeForm onCancel={() => setShowNewCode(false)} onSaved={() => { setShowNewCode(false); load(); }} />
                    )}
                </Section>
            </div>
        </AdminShell>
    );
};

const EpsonSection = ({ settings, setSettings }) => {
    const [testing, setTesting] = useState(false);
    const [queue, setQueue] = useState(null);
    const [loadingQueue, setLoadingQueue] = useState(false);
    const printerId = (settings.epson_printer_id || "counter").trim() || "counter";

    const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
    const pollUrl = `${apiBase}/api/print/poll?printer_id=${encodeURIComponent(printerId)}`;
    const ackUrl = `${apiBase}/api/print/ack`;

    const fireTest = async () => {
        setTesting(true);
        try {
            const r = await adminPrintTest(printerId);
            alert(`Test slip queued for printer "${printerId}".\nQueued size: ${r.queued_size}\n\nIf the printer is paired correctly it should print within the polling interval (default 60s).`);
        } catch (e) {
            alert(e.response?.data?.detail || "Test print failed");
        } finally { setTesting(false); }
    };

    const loadQueue = async () => {
        setLoadingQueue(true);
        try {
            const r = await adminPrintRecent(printerId, 10);
            setQueue(r.jobs);
        } catch (e) {
            alert(e.response?.data?.detail || "Failed to load queue");
        } finally { setLoadingQueue(false); }
    };

    const copy = (text, label) => {
        navigator.clipboard?.writeText(text);
        const el = document.createElement("div");
        el.textContent = `${label} copied ✓`;
        el.style.cssText = "position:fixed;bottom:24px;left:50%;transform:translateX(-50%);background:#111;color:#fff;padding:10px 18px;font-size:13px;font-family:monospace;z-index:9999;border:1px solid #d4af37";
        document.body.appendChild(el);
        setTimeout(() => el.remove(), 1400);
    };

    return (
        <Section eyebrow="// Printer" title="Epson Server Direct Print">
            <p className="text-muted text-[13px] mb-4">
                Pair your Epson <strong>TM-m30II</strong> (or any ePOS-Print compatible printer) directly to this cloud — no local agent. The printer polls these URLs on the cadence you set in its web UI. Every paid order auto-queues one slip per brand.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                <div>
                    <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted block mb-2">Printer ID (route slips here)</label>
                    <input
                        className="input"
                        placeholder="counter"
                        value={settings.epson_printer_id || ""}
                        onChange={(e) => setSettings({ ...settings, epson_printer_id: e.target.value })}
                        data-testid="epson-printer-id"
                    />
                    <p className="font-mono text-[10px] text-muted mt-1.5">Default: <code>counter</code>. Use different IDs if you ever run multiple printers.</p>
                </div>
                <div className="flex flex-col gap-2 justify-end">
                    <button
                        onClick={fireTest}
                        disabled={testing}
                        className="btn-secondary text-sm"
                        data-testid="epson-test-print"
                    >
                        {testing ? "Queuing…" : "Queue test slip →"}
                    </button>
                    <button
                        onClick={loadQueue}
                        disabled={loadingQueue}
                        className="btn-ghost text-sm"
                        data-testid="epson-load-queue"
                    >
                        {loadingQueue ? "Loading…" : "View recent jobs"}
                    </button>
                </div>
            </div>

            <div className="border border-[var(--border)] p-4 space-y-3 bg-[rgba(212,175,55,0.04)]" data-testid="epson-setup-block">
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-gold">// Printer setup — paste these into the TM-m30II web UI</p>
                <ol className="text-[13px] space-y-2 list-decimal pl-4 text-muted">
                    <li>Open the printer&apos;s IP in a browser (find it from the self-test slip). Sign in (default password is the printer&apos;s serial number).</li>
                    <li>Go to <strong>Configuration → ePOS-Print → Server Direct Print</strong>.</li>
                    <li>Enable it, then fill in the URL below.</li>
                    <li>Set polling interval to <strong>60s</strong> (or 30s for faster slips). Save and reboot.</li>
                </ol>

                <div className="mt-4">
                    <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted block mb-1">Server URL</label>
                    <div className="flex gap-2">
                        <code className="input flex-1 text-[11px] font-mono break-all py-2" data-testid="epson-poll-url">{pollUrl}</code>
                        <button onClick={() => copy(pollUrl, "URL")} className="btn-secondary text-xs px-3">Copy</button>
                    </div>
                </div>

                <div className="mt-3">
                    <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted block mb-1">Acknowledgement URL (some firmwares need this separately)</label>
                    <div className="flex gap-2">
                        <code className="input flex-1 text-[11px] font-mono break-all py-2" data-testid="epson-ack-url">{ackUrl}</code>
                        <button onClick={() => copy(ackUrl, "ACK URL")} className="btn-secondary text-xs px-3">Copy</button>
                    </div>
                </div>
            </div>

            {queue && (
                <div className="mt-5 border border-[var(--border)] divide-y divide-[var(--border)]" data-testid="epson-queue-table">
                    {queue.length === 0 && (
                        <div className="p-4 text-center text-muted text-sm">No print jobs in queue.</div>
                    )}
                    {queue.map((j) => (
                        <div key={j.id} className="px-4 py-2 flex items-center gap-3 text-[12px]">
                            <span className="font-mono text-muted w-20">#{j.order_number || "—"}</span>
                            <span className="font-mono text-muted w-16">{j.brand_token}</span>
                            <span className={`font-mono uppercase text-[10px] tracking-[0.18em] flex-1 ${
                                j.status === "done" ? "text-emerald-500"
                                : j.status === "failed" ? "text-[#ff5246]"
                                : j.status === "in_flight" ? "text-gold"
                                : "text-muted"
                            }`}>{j.status}</span>
                            <span className="font-mono text-muted text-[10px]">{(j.created_at || "").slice(11, 19)}</span>
                        </div>
                    ))}
                </div>
            )}
        </Section>
    );
};

// API helper for Star CloudPRNT test print
const StarSection = ({ settings, setSettings }) => {
    const [testing, setTesting] = useState(false);
    const enabled = !!(settings.star_printer_id || "").trim();
    const printerId = (settings.star_printer_id || "star-counter").trim() || "star-counter";

    const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
    const pollUrl = `${apiBase}/api/cloudprnt/poll?printer_id=${encodeURIComponent(printerId)}`;

    const fireTest = async () => {
        setTesting(true);
        try {
            const r = await fetch(`${apiBase}/api/admin/print/test?printer_id=${encodeURIComponent(printerId)}&kind=star_cloudprnt`, { method: "POST" });
            const data = await r.json();
            alert(`Test slip queued for Star printer "${printerId}".\nQueued size: ${data.queued_size}\n\nIf the printer is paired correctly it should print within ~5 seconds (default CloudPRNT polling interval).`);
        } catch (e) {
            alert(e.message || "Test print failed");
        } finally { setTesting(false); }
    };

    const clearQueue = async () => {
        if (!window.confirm(`Purge ALL pending / in-flight print jobs for "${printerId}"?\n\nUse this after a slip-format change to flush stale payloads from previous builds. Completed jobs are kept for audit.`)) return;
        setTesting(true);
        try {
            const r = await fetch(`${apiBase}/api/admin/print/queue?printer_id=${encodeURIComponent(printerId)}`, { method: "DELETE" });
            const data = await r.json();
            alert(`Cleared ${data.deleted} job(s) from the queue.\n\nNext slip the printer polls will use the CURRENT slip format.`);
        } catch (e) {
            alert(e.message || "Clear queue failed");
        } finally { setTesting(false); }
    };

    const copy = (text, label) => {
        navigator.clipboard?.writeText(text);
        const el = document.createElement("div");
        el.textContent = `${label} copied ✓`;
        el.style.cssText = "position:fixed;bottom:24px;left:50%;transform:translateX(-50%);background:#111;color:#fff;padding:10px 18px;font-size:13px;font-family:monospace;z-index:9999;border:1px solid #d4af37";
        document.body.appendChild(el);
        setTimeout(() => el.remove(), 1400);
    };

    return (
        <Section eyebrow="// Printer · Star Micronics" title="Star CloudPRNT (TSP143IIIW)">
            <p className="text-muted text-[13px] mb-4">
                Pair a <strong>Star Micronics</strong> printer (TSP143IIIW, mC-Print3, etc.) using their <strong>CloudPRNT</strong> protocol. Printer is configured via the Star app once with a single URL — no Windows PC, no drivers, no SSL flags. Can run in parallel with the Epson (both will print every paid order) so you can A/B test before retiring one.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                <div>
                    <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted block mb-2">Star printer ID (leave blank to disable)</label>
                    <input
                        className="input"
                        placeholder="star-counter"
                        value={settings.star_printer_id || ""}
                        onChange={(e) => setSettings({ ...settings, star_printer_id: e.target.value })}
                        data-testid="star-printer-id"
                    />
                    <p className="font-mono text-[10px] text-muted mt-1.5">Suggested: <code>star-counter</code>. Empty = Star printer disabled, only Epson prints.</p>
                </div>
                <div className="flex flex-col gap-2 justify-end">
                    <button
                        onClick={fireTest}
                        disabled={testing || !enabled}
                        className="btn-secondary text-sm"
                        data-testid="star-test-print"
                        title={!enabled ? "Set a printer ID first" : "Queue a test slip"}
                    >
                        {testing ? "Queuing…" : enabled ? "Queue test slip →" : "Disabled"}
                    </button>
                    <button
                        onClick={clearQueue}
                        disabled={testing || !enabled}
                        className="text-[12px] font-mono uppercase tracking-[0.15em] px-3 py-2 border border-[var(--border)] text-muted hover:text-red-400 hover:border-red-400 transition-colors"
                        data-testid="star-clear-queue"
                        title="Purge all pending print jobs (use after a slip-format change to flush stale payloads)"
                    >
                        Clear queue
                    </button>
                </div>
            </div>

            {enabled && (
                <div className="border border-[var(--border)] p-4 space-y-3 bg-[rgba(212,175,55,0.04)]" data-testid="star-setup-block">
                    <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-gold">// Star TSP143IIIW setup — 5 steps</p>
                    <ol className="text-[13px] space-y-2 list-decimal pl-4 text-muted">
                        <li>Plug the printer into your <strong>router</strong> (Ethernet) — wait 30s for it to get an IP.</li>
                        <li>On your phone, install the free <strong>&quot;Star Quick Setup Utility&quot;</strong> app (App Store / Play Store). Connect to the same Wi-Fi as the printer.</li>
                        <li>Open the app → it auto-discovers the printer → tap it → <strong>CloudPRNT settings</strong>.</li>
                        <li>Paste the Server URL below. Leave authentication blank. Set polling interval to <strong>5s</strong> (or up to 30s if you want).</li>
                        <li>Tap <strong>Save → Reboot printer</strong>. Done.</li>
                    </ol>

                    <div className="mt-4">
                        <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted block mb-1">CloudPRNT Server URL</label>
                        <div className="flex gap-2">
                            <code className="input flex-1 text-[11px] font-mono break-all py-2" data-testid="star-poll-url">{pollUrl}</code>
                            <button onClick={() => copy(pollUrl, "URL")} className="btn-secondary text-xs px-3">Copy</button>
                        </div>
                        <p className="font-mono text-[10px] text-muted mt-1.5">Star printers use the SAME URL for poll / fetch / acknowledge — paste only this one.</p>
                    </div>
                </div>
            )}
        </Section>
    );
};

const RecentSlipsSection = () => {
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [reprinting, setReprinting] = useState(null);
    const [lastLoadedAt, setLastLoadedAt] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const r = await adminPrintRecent(null, 20);
            setJobs(r.jobs || []);
            setLastLoadedAt(new Date());
        } catch (e) {
            console.error("Failed to load recent slips", e);
        } finally { setLoading(false); }
    };

    useEffect(() => {
        load();
        // Auto-refresh every 5s so the panel updates as slips flow through
        const t = setInterval(load, 5000);
        return () => clearInterval(t);
    }, []);

    const reprint = async (jobId, orderNumber) => {
        if (!window.confirm(`Reprint slips for order ${orderNumber || jobId.slice(0, 8)}?`)) return;
        setReprinting(jobId);
        try {
            await adminPrintReprint(jobId);
            await load();
        } catch (e) {
            alert(e.response?.data?.detail || "Reprint failed");
        } finally { setReprinting(null); }
    };

    const pill = (status) => {
        const map = {
            queued: { label: "QUEUED", bg: "#3a3a3a", fg: "#a8a8a8" },
            in_flight: { label: "PRINTING", bg: "#5c4a1a", fg: "#f5c847" },
            done: { label: "DONE", bg: "#1a3d1a", fg: "#7ac97a" },
            failed: { label: "FAILED", bg: "#4a1a1a", fg: "#ff8877" },
        };
        const s = map[status] || { label: status?.toUpperCase() || "?", bg: "#2a2a2a", fg: "#888" };
        return (
            <span
                className="font-mono text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-sm"
                style={{ background: s.bg, color: s.fg }}
                data-testid={`slip-status-${status}`}
            >
                {s.label}
            </span>
        );
    };

    const relTime = (iso) => {
        if (!iso) return "—";
        const diffMs = Date.now() - new Date(iso).getTime();
        const s = Math.floor(diffMs / 1000);
        if (s < 60) return `${s}s ago`;
        const m = Math.floor(s / 60);
        if (m < 60) return `${m}m ago`;
        const h = Math.floor(m / 60);
        if (h < 24) return `${h}h ago`;
        return new Date(iso).toLocaleDateString();
    };

    return (
        <Section eyebrow="// Diagnostics" title="Recent slips">
            <div className="flex items-baseline justify-between mb-3">
                <p className="text-muted text-[13px]">
                    Last 20 kitchen slips across every printer. Auto-refreshes every 5 seconds. Click <span className="text-gold">Reprint</span> to re-queue a specific ticket.
                </p>
                <button
                    onClick={load}
                    disabled={loading}
                    className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted hover:text-gold"
                    data-testid="recent-slips-refresh"
                >
                    {loading ? "Loading…" : lastLoadedAt ? `Refreshed ${relTime(lastLoadedAt.toISOString())}` : "Refresh"}
                </button>
            </div>
            <div
                className="border border-[var(--border)] divide-y divide-[var(--border)]"
                data-testid="recent-slips-table"
            >
                {jobs.length === 0 ? (
                    <div className="p-6 text-center text-muted font-mono text-[12px]">
                        {loading ? "Loading…" : "No print jobs yet."}
                    </div>
                ) : jobs.map((j) => (
                    <div
                        key={j.id}
                        className="px-4 py-3 flex items-center gap-4"
                        data-testid={`slip-row-${j.id}`}
                    >
                        <div className="w-[80px] shrink-0">
                            <span className="font-display text-[15px] text-[var(--fg)]">#{j.order_number || "—"}</span>
                        </div>
                        <div className="w-[110px] shrink-0">{pill(j.status)}</div>
                        <div className="w-[85px] shrink-0 font-mono text-[10px] uppercase tracking-[0.15em] text-muted">
                            {j.printer_kind === "star_cloudprnt" ? "★ Star" : j.printer_kind === "epson_sdp" ? "◆ Epson" : "?"}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-mono text-[11px] text-[var(--fg)] truncate">
                                {j.brand_name || j.brand_token || "—"}
                            </div>
                            <div className="font-mono text-[10px] text-muted">
                                {relTime(j.created_at)}
                                {j.attempts > 0 && ` · ${j.attempts} attempt${j.attempts === 1 ? "" : "s"}`}
                                {j.reprinted_from && " · reprint"}
                            </div>
                        </div>
                        <button
                            onClick={() => reprint(j.id, j.order_number)}
                            disabled={reprinting === j.id || j.status === "queued" || j.status === "in_flight"}
                            className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted hover:text-gold disabled:opacity-40 disabled:cursor-not-allowed"
                            data-testid={`slip-reprint-${j.id}`}
                            title={j.status === "queued" || j.status === "in_flight" ? "Already pending" : "Clone this job as a fresh queued row"}
                        >
                            {reprinting === j.id ? "…" : "Reprint"}
                        </button>
                    </div>
                ))}
            </div>
        </Section>
    );
};

const NewCodeForm = ({ onCancel, onSaved }) => {
    const [form, setForm] = useState({ code: "", type: "percent", value: 10, min_order: 0, max_uses: "", expires_at: "", active: true });
    const [saving, setSaving] = useState(false);
    const submit = async () => {
        if (!form.code.trim()) return alert("Code required");
        setSaving(true);
        try {
            await adminCreateDiscountCode({
                code: form.code.trim(),
                type: form.type,
                value: parseFloat(form.value) || 0,
                min_order: parseFloat(form.min_order) || 0,
                max_uses: form.max_uses ? parseInt(form.max_uses, 10) : null,
                expires_at: form.expires_at || null,
                active: form.active,
            });
            onSaved();
        } catch (e) {
            alert(e.response?.data?.detail || "Save failed");
        } finally { setSaving(false); }
    };
    return (
        <div className="border border-[var(--gold)] p-4 mb-3 space-y-3" data-testid="new-code-form">
            <div className="grid grid-cols-3 gap-3">
                <input className="input uppercase" placeholder="WELCOME10" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} data-testid="new-code-code" />
                <select className="input" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                    <option value="percent">Percentage</option>
                    <option value="fixed">Fixed (£)</option>
                </select>
                <input className="input" type="number" step="0.5" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} placeholder={form.type === "percent" ? "10 (%)" : "5.00"} />
            </div>
            <div className="grid grid-cols-3 gap-3">
                <input className="input" type="number" step="0.5" value={form.min_order} onChange={(e) => setForm({ ...form, min_order: e.target.value })} placeholder="Min order £" />
                <input className="input" type="number" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} placeholder="Max uses (blank = ∞)" />
                <input className="input" type="date" value={form.expires_at?.slice(0, 10) || ""} onChange={(e) => setForm({ ...form, expires_at: e.target.value ? new Date(e.target.value + "T23:59:59Z").toISOString() : "" })} />
            </div>
            <div className="flex gap-2">
                <button onClick={submit} disabled={saving} className="btn-primary" data-testid="save-new-code">{saving ? "Saving…" : "Save code"}</button>
                <button onClick={onCancel} className="btn-ghost text-sm">Cancel</button>
            </div>
        </div>
    );
};

const DiscountRow = ({ code, onSaved }) => {
    const toggleActive = async () => {
        await adminUpdateDiscountCode(code.id, { active: !code.active });
        onSaved();
    };
    const remove = async () => {
        if (!window.confirm(`Delete code "${code.code}"?`)) return;
        await adminDeleteDiscountCode(code.id);
        onSaved();
    };
    return (
        <div className="px-4 py-3 flex items-center gap-4" data-testid={`code-row-${code.code}`}>
            <div className="flex-1">
                <div className="flex items-baseline gap-3">
                    <span className="font-display text-[16px]">{code.code}</span>
                    {!code.active && <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#ff5246]">INACTIVE</span>}
                </div>
                <div className="font-mono text-[11px] text-muted">
                    {code.type === "percent" ? `${code.value}% off` : `${formatMoney(code.value)} off`}
                    {code.min_order > 0 && ` · min ${formatMoney(code.min_order)}`}
                    {code.max_uses != null && ` · used ${code.used_count}/${code.max_uses}`}
                    {code.expires_at && ` · expires ${code.expires_at.slice(0, 10)}`}
                </div>
            </div>
            <button onClick={toggleActive} className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted hover:text-gold">
                {code.active ? "Disable" : "Enable"}
            </button>
            <button onClick={remove} className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted hover:text-[#ff5246]">Delete</button>
        </div>
    );
};

export default AdminBusinessSettings;
